import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { BRAND, STATUS, FONT_MONO } from 'Utils/styleTokens';
import { engineLabels } from 'Utils';
import { formatBytes } from 'Utils/fileTypeUtils';

// The only role we can actually assert is grounded in the readOnly flag: a read-only
// member is a source of record. Everything else gets no role pill — we can't tell a
// sanctioned replica from shadow data, so we don't claim either.
const roleOf = (member) => {
  if (member.readOnly) return { label: 'SOURCE', color: '#6D28D9', bg: 'var(--op-mask-bg)' };
  return null;
};

const locationLine = (location) => {
  if (!location) return null;
  const parts = [location.cloud, location.region, location.storageType].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * Identity cluster panel — mockup data-source-hub section F. Members of the one
 * cluster containing this source, each with location, latest-profile size, and
 * schema match %. The viewed source is pinned to the top; read-only members carry
 * a SOURCE pill (the one role the data actually supports). Singleton clusters
 * render as "no other copies found".
 */
function IdentityClusterPanel({ cluster, currentId }) {
  if (!cluster) return null;
  const allMembers = cluster.dataSources || [];
  // The viewed source first, everything else in its original order below.
  const members = [
    ...allMembers.filter((m) => String(m.dataSourceId) === String(currentId)),
    ...allMembers.filter((m) => String(m.dataSourceId) !== String(currentId)),
  ];

  return (
    <div className='mb-3'>
      <h5 className='text-left sg-eyebrow'>Fingerprint · identity cluster</h5>
      <Card>
        <Card.Body>
          <div className='d-flex align-items-center mb-1' style={{ gap: 10 }}>
            <strong>Identity cluster</strong>
            {cluster.displayName && (
              <span className='sg-tag' style={{ fontFamily: FONT_MONO }}>{cluster.displayName}</span>
            )}
            <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
              {members.length} member{members.length === 1 ? '' : 's'} share this schema fingerprint
            </span>
          </div>
          <div className='sg-small mb-3' style={{ color: 'var(--ink-500)' }}>
            Where copies of this identity family live.
          </div>
          {members.length <= 1 ? (
            <div className='sg-small' style={{ color: 'var(--ink-400)' }}>
              No other copies of this schema were found. Fingerprint more sources to detect additional copies.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--ink-150)', borderRadius: 'var(--r-md)' }}>
              {members.map((member) => {
                const isCurrent = String(member.dataSourceId) === String(currentId);
                const role = roleOf(member);
                const locLine = locationLine(member.location);
                return (
                  <div
                    key={member.dataSourceId}
                    className='d-flex align-items-center'
                    style={{
                      gap: 12,
                      padding: '10px 12px',
                      borderTop: '1px solid var(--ink-100)',
                      background: isCurrent ? '#F5F8FF' : 'transparent',
                    }}
                    data-testid={`cluster-member-${member.dataSourceId}`}
                  >
                    <span style={{ color: member.readOnly ? STATUS.warning.dot : 'var(--ink-300)', flex: 'none' }}>
                      {member.readOnly ? <FaLock size='0.9em' /> : <FaLockOpen size='0.9em' />}
                    </span>
                    <div style={{ width: 220, flex: 'none' }}>
                      {isCurrent ? (
                        <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}>
                          {member.name} <span className='sg-small' style={{ color: 'var(--ink-400)' }}>(viewing)</span>
                        </span>
                      ) : (
                        <Link
                          to={`/landscape/riskprofile/${encodeURIComponent(member.dataSourceId)}`}
                          style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                        >
                          {member.name}
                        </Link>
                      )}
                      <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
                        {engineLabels[member.engine] || member.engine || member.datasourceType}
                      </div>
                    </div>
                    {role && (
                      <span
                        className='sg-pill sg-pill--nodot'
                        style={{ background: role.bg, color: role.color, fontSize: 10, flex: 'none' }}
                      >
                        {role.label}
                      </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {locLine && <div className='sg-small'>{locLine}</div>}
                      {member.location?.path && (
                        <div
                          className='sg-small'
                          style={{
                            fontFamily: FONT_MONO,
                            color: 'var(--ink-500)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={member.location.path}
                        >
                          {member.location.path}
                        </div>
                      )}
                      {!locLine && !member.location?.path && member.location?.host && (
                        <div className='sg-small' style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
                          {member.location.host}
                        </div>
                      )}
                    </div>
                    <span
                      className='sg-small'
                      style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)', width: 70, textAlign: 'right', flex: 'none' }}
                      title='Size at latest fingerprint snapshot'
                      data-testid={`cluster-member-size-${member.dataSourceId}`}
                    >
                      {member.totalSizeBytes != null ? formatBytes(member.totalSizeBytes) : '—'}
                    </span>
                    <span style={{ fontFamily: FONT_MONO, fontWeight: 600, flex: 'none' }}>
                      {member.similarityToRepresentative != null
                        ? `${Math.round(member.similarityToRepresentative * 100)}%`
                        : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default IdentityClusterPanel;
